import { Flex, Text, Tooltip } from '@chakra-ui/react';
import React from 'react';
import { Provider, Practice } from '../../../types/practices/types';

type ProviderBridgeItem = Provider['providerBridge'][0];

type PacStatus =
  | 'on_pac'
  | 'not_on_pac'
  | 'hospital_follow_up_only'
  | 'sick_cross_coverage_only'
  | 'hospital_follow_up_and_sick_cross_coverage_only'
  | 'mixed';

const PAC_STATUS_CONFIG: Record<
  PacStatus,
  { label: string; color: string; tooltipBase: string }
> = {
  on_pac: {
    label: 'On PAC',
    color: '#38A169',
    tooltipBase: 'On PAC',
  },
  not_on_pac: {
    label: 'Not on PAC',
    color: '#E53E3E',
    tooltipBase: 'Not on PAC',
  },
  hospital_follow_up_only: {
    label: 'Hospital Follow-Up Only',
    color: '#D69E2E',
    tooltipBase: 'Hospital Follow-Up Only',
  },
  sick_cross_coverage_only: {
    label: 'Sick Cross Coverage Only',
    color: '#D69E2E',
    tooltipBase: 'Sick Cross Coverage Only',
  },
  hospital_follow_up_and_sick_cross_coverage_only: {
    label: 'Hospital FU/Sick Cross Coverage Only',
    color: '#D69E2E',
    tooltipBase: 'Hospital Follow-Up and Sick Cross Coverage Only',
  },
  mixed: {
    label: 'Mixed',
    color: '#D69E2E',
    tooltipBase: 'Mixed',
  },
};

interface ProviderPacStatusProps {
  providerBridge: ProviderBridgeItem[] | undefined;
  relatedPractices?: Practice[] | null;
}

const formatDisplayDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const getPracticeName = (
  practiceId: string,
  relatedPractices?: Practice[] | null
): string => {
  if (!relatedPractices) return '';
  const practice = relatedPractices.find((p) => p.id === practiceId);
  return practice?.name || '';
};

const buildTerminatedTooltipLabel = (
  bridgeItems: ProviderBridgeItem[],
  relatedPractices?: Practice[] | null
): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const practiceInfo = bridgeItems
    .map((item) => {
      const practiceName = getPracticeName(item.practiceId, relatedPractices);
      const endDate = item.endDate ? formatDisplayDate(item.endDate) : '';
      const displayDate = item.displayDate
        ? formatDisplayDate(item.displayDate)
        : null;

      if (practiceName && endDate) {
        if (displayDate && item.displayDate && new Date(item.displayDate) >= today) {
          return `${practiceName} (Terminated: ${endDate}, Display until: ${displayDate})`;
        }
        return `${practiceName} (Terminated: ${endDate})`;
      }
      return null;
    })
    .filter(Boolean)
    .join('\n');

  return practiceInfo ? `Terminated at:\n${practiceInfo}` : 'Provider has been terminated';
};

const derivePacStatus = (activeBridgeItems: ProviderBridgeItem[]): PacStatus => {
  if (activeBridgeItems.some((item) => item.onPac)) return 'on_pac';

  const hasHfus = activeBridgeItems.some((item) => item.hospitalFollowupPac);
  const hasSick = activeBridgeItems.some((item) => item.sickCrossCoveragePac);

  if (hasHfus && hasSick) {
    const contributing = activeBridgeItems.filter(
      (item) => item.hospitalFollowupPac || item.sickCrossCoveragePac
    );
    const allHaveBoth = contributing.every(
      (item) => item.hospitalFollowupPac && item.sickCrossCoveragePac
    );
    return allHaveBoth ? 'hospital_follow_up_and_sick_cross_coverage_only' : 'mixed';
  }
  if (hasHfus) return 'hospital_follow_up_only';
  if (hasSick) return 'sick_cross_coverage_only';
  return 'not_on_pac';
};

const getContributingItems = (
  activeBridgeItems: ProviderBridgeItem[],
  status: PacStatus
): ProviderBridgeItem[] => {
  switch (status) {
    case 'on_pac':
      return activeBridgeItems.filter((item) => item.onPac);
    case 'hospital_follow_up_only':
      return activeBridgeItems.filter((item) => item.hospitalFollowupPac);
    case 'sick_cross_coverage_only':
      return activeBridgeItems.filter((item) => item.sickCrossCoveragePac);
    case 'hospital_follow_up_and_sick_cross_coverage_only':
    case 'mixed':
      return activeBridgeItems.filter(
        (item) => item.hospitalFollowupPac || item.sickCrossCoveragePac
      );
    case 'not_on_pac':
      return activeBridgeItems;
  }
};

const buildPacTooltipLabel = (
  bridgeItems: ProviderBridgeItem[],
  relatedPractices: Practice[] | null | undefined,
  statusBase: string
): string => {
  const practiceInfo = bridgeItems
    .map((item) => getPracticeName(item.practiceId, relatedPractices) || null)
    .filter(Boolean)
    .join(', ');

  const fallback = statusBase.charAt(0).toLowerCase() + statusBase.slice(1);
  return practiceInfo ? `${statusBase} at: ${practiceInfo}` : `Provider is ${fallback}`;
};

const buildCombinedTooltipLabel = (
  contributingItems: ProviderBridgeItem[],
  relatedPractices: Practice[] | null | undefined
): string => {
  const group = (filter: (item: ProviderBridgeItem) => boolean) =>
    contributingItems
      .filter(filter)
      .map((item) => getPracticeName(item.practiceId, relatedPractices))
      .filter(Boolean)
      .join(', ');

  const hfusOnly = group((item) => !!item.hospitalFollowupPac && !item.sickCrossCoveragePac);
  const sickOnly = group((item) => !item.hospitalFollowupPac && !!item.sickCrossCoveragePac);
  const both     = group((item) => !!item.hospitalFollowupPac && !!item.sickCrossCoveragePac);

  const lines = [
    hfusOnly && `Hospital Follow-Up Only: ${hfusOnly}`,
    sickOnly && `Sick Cross Coverage Only: ${sickOnly}`,
    both     && `Hospital FU & Sick Cross Coverage: ${both}`,
  ].filter(Boolean).join('\n');

  return lines || 'Provider covers hospital follow-up and sick cross coverage';
};

const ProviderPacStatus: React.FC<ProviderPacStatusProps> = ({
  providerBridge,
  relatedPractices,
}) => {
  if (!providerBridge) return null;

  if (providerBridge.length === 0) {
    return (
      <Tooltip
        label="Provider has been terminated and is no longer active"
        placement="top"
        hasArrow
      >
        <Flex
          bg="#E53E3E"
          borderRadius="0.25rem"
          height="1.5rem"
          alignItems="center"
          justifyContent="center"
          px="0.5rem"
          py="0.25rem"
          cursor="help"
        >
          <Text color="white" fontSize="0.75rem" fontWeight={600} textTransform="uppercase">
            Terminated
          </Text>
        </Flex>
      </Tooltip>
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const terminatedBridgeItems = providerBridge.filter((item) => {
    if (!item.endDate) return false;
    const endDate = new Date(item.endDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate <= today;
  });

  const activeBridgeItems = providerBridge.filter((item) => {
    if (!item.endDate) return true;
    const endDate = new Date(item.endDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate > today;
  });

  if (terminatedBridgeItems.length === providerBridge.length) {
    return (
      <Tooltip
        label={buildTerminatedTooltipLabel(terminatedBridgeItems, relatedPractices)}
        placement="top"
        hasArrow
      >
        <Flex
          bg="#E53E3E"
          borderRadius="0.3125rem"
          minH="1.5rem"
          alignItems="center"
          justifyContent="center"
          px="0.5rem"
          py="0.25rem"
          cursor="help"
          _hover={{ filter: 'brightness(1.05)', transform: 'translateY(-1px)' }}
          transition="filter 0.15s ease, transform 0.15s ease"
          data-testid="chip"
        >
          <Text
            color="white"
            fontSize="0.75rem"
            fontWeight={600}
            lineHeight="1.2"
            textAlign="center"
            whiteSpace="normal"
            wordBreak="break-word"
            minW="4.5rem"
          >
            Terminated
          </Text>
        </Flex>
      </Tooltip>
    );
  }

  const pacStatus = derivePacStatus(activeBridgeItems);
  const { label, color, tooltipBase } = PAC_STATUS_CONFIG[pacStatus];
  const contributingItems = getContributingItems(activeBridgeItems, pacStatus);
  const tooltip =
    pacStatus === 'hospital_follow_up_and_sick_cross_coverage_only' || pacStatus === 'mixed'
      ? buildCombinedTooltipLabel(contributingItems, relatedPractices)
      : buildPacTooltipLabel(contributingItems, relatedPractices, tooltipBase);

  return (
    <Tooltip label={tooltip} placement="top" hasArrow>
      <Flex
        bg={color}
        borderRadius="0.3125rem"
        minH="1.5rem"
        alignItems="center"
        justifyContent="center"
        px="0.5rem"
        py="0.25rem"
        cursor="help"
        _hover={{ filter: 'brightness(1.05)', transform: 'translateY(-1px)' }}
        transition="filter 0.15s ease, transform 0.15s ease"
        data-testid="chip"
      >
        <Text
          color="white"
          fontSize="0.75rem"
          fontWeight={600}
          lineHeight="1.2"
          textAlign="center"
          whiteSpace="normal"
          wordBreak="break-word"
          minW="4.5rem"
        >
          {label}
        </Text>
      </Flex>
    </Tooltip>
  );
};

export default ProviderPacStatus;
